import React from "react";
import { Button, Input, Select, Space, Typography } from "../ui";
import { spacing } from "../settings/settingsListDesign";

// Optisches Signal (HHD/Flickercode): ein Byte sind zwei Nibbles, jedes Nibble zwei Frames; Takt- plus vier Datenbalken.
const DEFAULT_CANVAS_WIDTH = 280;
const DEFAULT_FREQUENCY_HZ = 10;
const CANVAS_HEIGHT = 80;
const HEX_DIGITS_PER_BYTE = 2;
const HEX_RADIX = 16;
const FRAMES_PER_NIBBLE = 2;
const BAR_COUNT = 5;
const BAR_MARGIN_RATIO = 0.1;
const BAR_WIDTH_RATIO = 0.8;
const BAR_TOP = 4;
const BAR_HEIGHT = 72;
const MS_PER_SECOND = 1000;

/** HHD optical framing: sync 0FFF, low nibble first, data bits LSB first, clock high/low.
 * Reference: HBCI4Java 4.1.17 FlickerRenderer (LGPL-2.1), org.kapott.hbci.manager.
 * This is an optical display; no connection to or reading from the user's device takes place.
 */
export function BankingFlickerCode({ code, tr }: { code: string; tr: (key: string) => string }) {
  const canvas = React.useRef<HTMLCanvasElement>(null);
  const [playing, setPlaying] = React.useState(false);
  const [width, setWidth] = React.useState(DEFAULT_CANVAS_WIDTH);
  const [frequency, setFrequency] = React.useState(DEFAULT_FREQUENCY_HZ);
  React.useEffect(() => {
    const context = canvas.current?.getContext("2d");
    if (!context || !/^(?:[0-9A-F]{2})+$/.test(code)) return;
    const framed = "0FFF" + code;
    const nibbles: number[] = [];
    for (let i = 0; i < framed.length; i += HEX_DIGITS_PER_BYTE) {
      nibbles.push(parseInt(framed[i + 1], HEX_RADIX), parseInt(framed[i], HEX_RADIX));
    }
    let frame = 0;
    const paint = () => {
      const nibble = nibbles[Math.floor(frame / FRAMES_PER_NIBBLE) % nibbles.length];
      // design-rule-exception: raw-color — the HHD optical signal requires black and white, independent of theme.
      context.fillStyle = "#000000";
      context.fillRect(0, 0, width, CANVAS_HEIGHT);
      for (let bar = 0; bar < BAR_COUNT; bar++) {
        const active = bar === 0 ? frame % FRAMES_PER_NIBBLE === 0 : (nibble & (1 << (bar - 1))) !== 0;
        // design-rule-exception: raw-color — protocol signal, not a UI theme color.
        context.fillStyle = active && playing ? "#ffffff" : "#000000";
        const cell = width / BAR_COUNT;
        context.fillRect(bar * cell + cell * BAR_MARGIN_RATIO, BAR_TOP, cell * BAR_WIDTH_RATIO, BAR_HEIGHT);
      }
      frame++;
    };
    paint();
    if (!playing) return;
    const timer = window.setInterval(paint, MS_PER_SECOND / frequency);
    return () => window.clearInterval(timer);
  }, [code, playing, width, frequency]);
  return <Space direction="vertical" style={{ width: "100%", marginBottom: spacing.md }}>
    <canvas ref={canvas} width={width} height={80} style={{ maxWidth: "100%" }} role="img" aria-label={tr("banking.opticalSignal")} />
    <Space wrap>
      <Button onClick={() => setPlaying((value) => !value)}>{tr(playing ? "banking.stop" : "banking.start")}</Button>
      <Typography.Text>{tr("banking.opticalWidth")}</Typography.Text>
      <Input type="range" min={120} max={360} step={10} value={width} onChange={(e) => setWidth(Number(e.target.value))} aria-label={tr("banking.opticalWidth")} />
      <Select value={frequency} onChange={setFrequency} aria-label={tr("banking.opticalSpeed")}
        options={[2, 5, 10, 15, 20].map((value) => ({ value, label: `${value} Hz` }))} />
    </Space>
  </Space>;
}
